import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import { Sort } from "@mui/icons-material";
import ActionFilter from "../../components/ActionFilter";
import dayjs from "dayjs";
import { limitChar } from "../../utils/common";
import { useNavigate } from "react-router-dom";
import routePaths from "../../router/route-paths";
import { useDispatch } from "react-redux";
import { deleteKaraoke, listKaraoke } from "../../http/karaoke";
import { setLoading, showToast } from "../../store/slices/common";
import { LadderChip } from "../../components/FormElements/LadderField";
import { normalizeFeedGroupSize } from "../../components/FormElements/FeedGroupSizeField";
import { KARAOKE_FORMATS, karaokeFormatFromPlayModes, karaokeFormatLabel } from "./karaokeHelpers";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import AvailabilityModal from "./AvailabilityModal";
import StickersModal from "./StickersModal";
import LiveAvailabilityCard from "../../components/LiveAvailabilityCard";
import UpcomingThemesCard from "./UpcomingThemesCard";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../utils/roleAccess";
import PageLayout from "../../components/PageLayout";
import CommonPagination from "../../components/Table/CommonPagination";

const PAGE_SIZE = 20;

const headerCellSx = {
  fontSize: {
    xs: "14px",
    sm: "15px",
    md: "15px",
    lg: "16px",
    xl: "16px",
  },
  color: "#000",
  fontWeight: 600,
  padding: {
    xs: "10px 5px",
    sm: "15px 10px",
    md: "15px 10px",
    lg: "20px 15px",
    xl: "20px 15px",
  },
  backgroundColor: "#e0e0e0",
};

const headerCellCenterSx = {
  ...headerCellSx,
  textAlign: "center",
};

const bodyCellSx = {
  fontSize: {
    xs: "12px",
    sm: "13px",
    md: "14px",
    lg: "15px",
    xl: "15px",
  },
  color: "#333",
  padding: {
    xs: "10px 5px",
    sm: "10px",
    md: "10px",
    lg: "10px 15px",
    xl: "10px 15px",
  },
};

const bodyCellCenterSx = {
  ...bodyCellSx,
  textAlign: "center",
};

const actionsCellSx = {
  ...bodyCellSx,
  paddingLeft: {
    xs: "5px",
    sm: "10px",
    md: "10px",
    lg: "15px",
    xl: "15px",
  },
  position: "relative",
};

// Server-side availability-status filters (match backend `status` param values)
const STATUS_FILTERS = [
  { value: "", label: "All", color: "primary" },
  { value: "upcoming", label: "Upcoming", color: "warning" },
  { value: "tbd", label: "Date TBD", color: "default" },
  { value: "active", label: "Active", color: "success" },
  { value: "always", label: "Always", color: "success" },
  { value: "expired", label: "Expired", color: "default" },
];

const InlineVocalPlayer = ({ url }) => {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = useState(false);

  if (!url) {
    return (
      <Typography variant="caption" color="textSecondary">—</Typography>
    );
  }

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  return (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
    <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} preload="none" />
    <Button
      size="small"
      variant="outlined"
      onClick={toggle}
      sx={{
        minWidth: "auto",
        px: 1,
        borderColor: "#142C5C",
        color: "#142C5C",
        fontSize: "12px",
        "&:hover": { borderColor: "#0f2247", backgroundColor: "rgba(20, 44, 92, 0.08)" },
      }}
    >
      {playing ? "Pause" : "Play"}
    </Button>
  </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  dialogContent: {
    maxWidth: "100%",
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    maxHeight: "calc(100vh - 135px)",
    minHeight: "calc(100vh - 135px)",
    boxShadow: "none !important",
  },
  table: {
    minWidth: "100%",
    border: "1px solid rgba(224, 224, 224, 1)",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  actionsCell: {
    textAlign: "center",
  },
  actionsHeader: {
    textAlign: "center",
    fontWeight: "bold",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  searchField: {
    width: "300px",
    "& .MuiInputLabel-root": {
      color: "#142C5C", // Label color
      borderRadius: "15px",
      height: "50px",
    },
    "& .MuiInputBase-formControl": {
      padding: "15px",
      display: "flex",
      alignItems: "center",
      "& input": {
        padding: 0,
      },
      "& fieldset": {
        borderColor: "#142C5C", // Border color when not focused
      },
      "&:hover fieldset": {
        borderColor: "#142C5C", // Border color on hover
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C", // Border color when focused
      },
    },
  },
  input: {
    marginBottom: "0 !important",
    "& label.Mui-focused": {
      color: "#142C5C",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#142C5C",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderColor: "#142C5C",
      },
      "&:hover fieldset": {
        borderColor: "#142C5C",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C",
      },
    },
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    borderColor: "#142C5C !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "10px",
  },
}));

const getAvailabilityStatus = (item) => {
  const now = new Date();
  const start = item.availabilityStartTime ? new Date(item.availabilityStartTime) : null;
  const end = item.availabilityEndTime ? new Date(item.availabilityEndTime) : null;

  if (item.isUnscheduled) return { label: "Date TBD", color: "default" };
  if (!start && !end) return { label: "Always", color: "success" };
  if (start && now < start) return { label: "Scheduled", color: "warning" };
  if (end && now > end) return { label: "Expired", color: "default" };
  return { label: "Active", color: "success" };
};

const ManageKaraoke = () => {
  const classes = useStyles();
  const [karaokes, setKaraokes] = useState([]);
  const [deleteId, setDeleteId] = useState("");
  const [availabilityKaraoke, setAvailabilityKaraoke] = useState(null);
  const [stickersKaraoke, setStickersKaraoke] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0); // 0-based, CommonPagination convention
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [, setTick] = useState(0);
  // sortBy "" = server default (priority order: Upcoming → TBD → Active → Always → Expired)
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { canViewActivity, isAdmin, hasFeature } = usePermissions();
  // The Karaoke Live schedule lives under /admin/settings, which is
  // admin-only; restricted accounts would get a 403 from it. Accounts that
  // cannot open the Themes page get the upcoming-themes list here instead,
  // so they know which theme they are adding songs for.
  const showLiveSchedule = isAdmin;
  const showUpcomingThemes = !hasFeature(PERMISSIONS.THEMES);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.removeItem("editKaraoke");
  }, []);

  const fetchKaraokesHandler = async () => {
    try {
      dispatch(setLoading(true));
      const res = await listKaraoke({
        limit: PAGE_SIZE,
        page: page + 1, // backend pages are 1-based
        searchBy: debouncedSearch || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortBy ? sortOrder : undefined,
        status: statusFilter || undefined,
      });
      const data = res?.data ?? res;
      let karaokesList = [];
      if (Array.isArray(data)) {
        karaokesList = data;
      } else if (data?.karaokes) {
        karaokesList = data.karaokes;
      } else if (data?.list) {
        karaokesList = data.list;
      } else if (data && typeof data === "object" && !Array.isArray(data)) {
        karaokesList = Array.isArray(data.data) ? data.data : [];
      }
      setKaraokes(karaokesList);
      setTotal(data?.total ?? res?.total ?? karaokesList.length);
    } catch (err) {
      console.error("Error fetching karaoke tracks:", err);
      dispatch(
        showToast({
          color: "error",
          msg: err?.response?.data?.message || err?.message || "Failed to load karaoke tracks",
        })
      );
      setKaraokes([]);
      setTotal(0);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchKaraokesHandler();
  }, [page, debouncedSearch, sortBy, sortOrder, statusFilter, refreshKey]);

  const refetchList = () => setRefreshKey((k) => k + 1);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(window.karaokeSearchTimeout);
    window.karaokeSearchTimeout = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(value);
    }, 300);
  };

  const handleStatusFilter = (value) => {
    setPage(0);
    setStatusFilter((prev) => (prev === value ? "" : value));
  };

  const openAddKaraokePage = () => {
    navigate(routePaths.uploadKaraoke);
  };

  const editHandler = (karaoke) => {
    localStorage.setItem("editKaraoke", JSON.stringify(karaoke));
    navigate(routePaths.uploadKaraoke);
  };

  const deleteHandler = async (karaoke) => {
    setDeleteId(karaoke?._id);
  };

  const confirmDeleteHandler = async () => {
    try {
      dispatch(setLoading(true));
      await deleteKaraoke(deleteId);
      setDeleteId("");
      // If the last row of a page was deleted, step back a page.
      if (karaokes.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        refetchList();
      }
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const cancelDeleteHandler = () => {
    setDeleteId("");
  };

  const updateAvailabilityHandler = (karaoke) => {
    setAvailabilityKaraoke(karaoke);
  };

  const closeAvailabilityModal = () => {
    setAvailabilityKaraoke(null);
  };

  const editStickersHandler = (karaoke) => {
    setStickersKaraoke(karaoke);
  };

  const closeStickersModal = () => {
    setStickersKaraoke(null);
  };

  const onAvailabilitySuccess = () => {
    refetchList();
  };

  // Server-side sort: 1st click = desc, 2nd = asc, 3rd = back to priority order
  const handleSort = (field) => {
    setPage(0);
    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("");
      setSortOrder("desc");
    }
  };

  return (
    <PageLayout
      title="Karaoke"
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            sx={{
              padding: {
                xs: "6px",
                sm: "6px 16px",
                md: "6px 16px",
                lg: "6px 16px",
                xl: "6px 16px",
              },
              backgroundColor: "#142C5C",
              color: "#fff",
              borderColor: "#0f2247",
              "&:hover": {
                backgroundColor: "#0f2247 !important",
              },
              borderRadius: "6px",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "13px",
                lg: "14px",
                xl: "14px",
              },
              height: {
                xs: "35px",
                sm: "40px",
                md: "40px",
                lg: "40px",
                xl: "40px",
              },
            }}
            variant="contained"
            onClick={openAddKaraokePage}
          >
            Add Karaoke
          </Button>
        </Box>
      }
      search={
        <>
          <TextField
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            sx={{
              width: "200px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: "#142C5C",
                },
                "&:hover fieldset": {
                  borderColor: "#142C5C",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#142C5C",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#142C5C" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant={sortBy === "availabilityStartTime" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSort("availabilityStartTime")}
              startIcon={<Sort />}
              sx={{
                backgroundColor: sortBy === "availabilityStartTime" ? "#142C5C" : "transparent",
                color: sortBy === "availabilityStartTime" ? "white" : "#142C5C",
                borderColor: "#142C5C",
                borderRadius: "4px",
                padding: "2px 6px",
                height: "32px",
                minWidth: "auto",
                fontSize: "10px",
                "&:hover": {
                  backgroundColor: sortBy === "availabilityStartTime" ? "#0f2247" : "rgba(20, 44, 92, 0.04)",
                  borderColor: "#0f2247",
                },
              }}
            >
              Start {sortBy === "availabilityStartTime" && (sortOrder === "desc" ? "↓" : "↑")}
            </Button>
            <Button
              variant={sortBy === "availabilityEndTime" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSort("availabilityEndTime")}
              startIcon={<Sort />}
              sx={{
                backgroundColor: sortBy === "availabilityEndTime" ? "#142C5C" : "transparent",
                color: sortBy === "availabilityEndTime" ? "white" : "#142C5C",
                borderColor: "#142C5C",
                borderRadius: "4px",
                padding: "2px 6px",
                height: "32px",
                minWidth: "auto",
                fontSize: "10px",
                "&:hover": {
                  backgroundColor: sortBy === "availabilityEndTime" ? "#0f2247" : "rgba(20, 44, 92, 0.04)",
                  borderColor: "#0f2247",
                },
              }}
            >
              End {sortBy === "availabilityEndTime" && (sortOrder === "desc" ? "↓" : "↑")}
            </Button>
          </Box>
        </>
      }
    >
      <>
        {/* Karaoke LIVE schedule: while open, songs marked Live can be battled.
            Songs marked Video stay recordable at the same time. */}
        {showLiveSchedule && (
          <Box sx={{ marginBottom: "20px" }}>
            <LiveAvailabilityCard
              title="Karaoke Live Availability"
              featureLabel="Live karaoke pagents"
              endpoint="/admin/settings/karaoke-live-availability"
              responseKey="karaokeLiveAvailability"
              showNotificationsToggle
              scheduleHelp="While a window is open, songs marked Live can be battled live. Songs marked Video can still be recorded at the same time. Outside a window, only recorded videos are available — live-only songs wait for the next window. Set Live and video, Video only, or Live only on each song below."
            />
          </Box>
        )}
        {showUpcomingThemes && (
          <Box sx={{ marginBottom: "20px" }}>
            <UpcomingThemesCard />
          </Box>
        )}
        <Box
          sx={{
            padding: "15px 15px 0 15px",
            borderRadius: "6px",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", pb: "12px" }}>
            {STATUS_FILTERS.map((option) => (
              <Chip
                key={option.value || "all"}
                label={option.label}
                size="small"
                clickable
                color={statusFilter === option.value ? option.color : "default"}
                variant={statusFilter === option.value ? "filled" : "outlined"}
                onClick={() => handleStatusFilter(option.value)}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell
                    sx={headerCellSx}
                  >
                    Thumbnail
                  </TableCell>
                  <TableCell
                    sx={headerCellSx}
                  >
                    Title of the Karaoke
                  </TableCell>
                  <TableCell
                    sx={headerCellSx}
                  >
                    Artist
                  </TableCell>
                  <TableCell sx={headerCellSx}>Theme</TableCell>
                  {canViewActivity && (
                    <TableCell sx={headerCellCenterSx}>Submissions</TableCell>
                  )}
                  <TableCell
                    sx={headerCellSx}
                  >
                    Start Date
                  </TableCell>
                  <TableCell
                    sx={headerCellSx}
                  >
                    End Date
                  </TableCell>
                  <TableCell
                    sx={headerCellCenterSx}
                  >
                    Availability
                  </TableCell>
                  <TableCell
                    sx={headerCellCenterSx}
                  >
                    Vocal Preview
                  </TableCell>
                  <TableCell
                    sx={headerCellCenterSx}
                  >
                    Format
                  </TableCell>
                  <TableCell
                    sx={headerCellCenterSx}
                  >
                    Mode
                  </TableCell>
                  <TableCell sx={headerCellCenterSx}>Feed group</TableCell>
                  <TableCell
                    sx={headerCellCenterSx}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {karaokes?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canViewActivity ? 13 : 12}
                      sx={{
                        ...bodyCellSx,
                        textAlign: "center",
                        height: "calc(100vh - 270px)",
                      }}
                    >
                      No karaokes found
                    </TableCell>
                  </TableRow>
                ) : (
                  karaokes.map((karaoke) => (
                    <TableRow key={karaoke?._id}>
                      <TableCell sx={bodyCellSx}>
                        <img
                          src={karaoke?.coverImage}
                          alt="logo"
                          style={{
                            paddingLeft: "20px",
                            height: "50px",
                            width: "50px",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={bodyCellSx}
                      >
                        {limitChar(karaoke?.title)}
                      </TableCell>
                      <TableCell
                        sx={bodyCellSx}
                      >
                        {limitChar(karaoke?.artist)}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {karaoke?.theme?.title ? (
                          limitChar(karaoke.theme.title)
                        ) : (
                          <span style={{ color: "#999" }}>—</span>
                        )}
                      </TableCell>
                      {canViewActivity && (
                        <TableCell sx={bodyCellCenterSx}>
                          {karaoke?.activeSubmissions || 0}
                        </TableCell>
                      )}
                      <TableCell
                        sx={bodyCellSx}
                      >
                        {karaoke?.availabilityStartTime
                          ? dayjs(karaoke.availabilityStartTime).format("DD MMM YYYY - h:mm A")
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={bodyCellSx}
                      >
                        {karaoke?.availabilityEndTime
                          ? dayjs(karaoke.availabilityEndTime).format("DD MMM YYYY - h:mm A")
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={bodyCellCenterSx}
                      >
                        {(() => {
                          const status = getAvailabilityStatus(karaoke);
                          return (
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                              variant={status.label === "Expired" ? "outlined" : "filled"}
                              sx={{ fontWeight: 500, fontSize: "12px" }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell
                        sx={bodyCellCenterSx}
                      >
                        <InlineVocalPlayer url={karaoke?.vocalAudio} />
                      </TableCell>
                      <TableCell sx={bodyCellCenterSx}>
                        {(() => {
                          const format = karaokeFormatFromPlayModes(karaoke?.playModes);
                          const formatColor =
                            format === KARAOKE_FORMATS.LIVE_ONLY
                              ? "#b91c1c"
                              : format === KARAOKE_FORMATS.LIVE
                                ? "#ef4444"
                                : "#142C5C";
                          return (
                            <Chip
                              label={karaokeFormatLabel(format)}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "11px",
                                color: "#fff",
                                backgroundColor: formatColor,
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={bodyCellCenterSx}>
                        {/* Player's choice, or hard-set at upload — change it from the edit form */}
                        <LadderChip
                          ladder={karaoke?.ladder}
                          ladderLocked={karaoke?.ladderLocked}
                          liveOnly={
                            karaokeFormatFromPlayModes(karaoke?.playModes) ===
                            KARAOKE_FORMATS.LIVE_ONLY
                          }
                        />
                      </TableCell>
                      <TableCell sx={bodyCellCenterSx}>
                        {normalizeFeedGroupSize(karaoke?.feedGroupSize) ??
                          "Default (3)"}
                      </TableCell>
                      <TableCell sx={actionsCellSx}>
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            top: "20%",
                          }}
                        >
                          <ActionFilter
                            index={karaoke}
                            onEdit={editHandler}
                            onDelete={deleteHandler}
                            onUpdateAvailability={updateAvailabilityHandler}
                            onEditStickers={editStickersHandler}
                            karaoke={true}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <Box sx={{ py: 1.5 }}>
              <CommonPagination
                rowsPerPage={PAGE_SIZE}
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
              />
            </Box>
          )}
        </Box>
        <ToastContainer />
        {deleteId && (
          <ConfirmDeleteModal
            onAccept={confirmDeleteHandler}
            onReject={cancelDeleteHandler}
            karaoke={true}
          />
        )}
        <AvailabilityModal
          open={!!availabilityKaraoke}
          onClose={closeAvailabilityModal}
          karaoke={availabilityKaraoke}
          onSuccess={onAvailabilitySuccess}
        />
        <StickersModal
          open={!!stickersKaraoke}
          onClose={closeStickersModal}
          karaoke={stickersKaraoke}
          onSuccess={refetchList}
        />
      </>
    </PageLayout>
  );
};

export default ManageKaraoke;
